import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import DeliveryNotice from '../components/DeliveryNotice';
import { usePricing } from '../lib/pricing';
import { useLang } from '../i18n/LanguageContext';
import { localizeKitName } from '../lib/productName';
import { localizeDescription } from '../data/productI18n';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const { lang } = useLang();
  const pricing = usePricing(lang);
  const navigate = useNavigate();

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const groupedItems = cart.reduce((acc, item) => {
      const existing = acc.find(i => i.id === item.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        acc.push({ ...item, quantity: 1 });
      }
      return acc;
    }, []);
    setCartItems(groupedItems);
  };

  useEffect(() => {
    loadCart();
  }, []);

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(productId);
      return;
    }

    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const product = cartItems.find(item => item.id === productId);
    const currentCount = cart.filter(item => item.id === productId).length;
    
    if (newQuantity > currentCount) {
      // Add more
      for (let i = 0; i < newQuantity - currentCount; i++) {
        cart.push(product);
      }
    } else {
      // Remove some
      const toRemove = currentCount - newQuantity;
      let removed = 0;
      const newCart = cart.filter(item => {
        if (item.id === productId && removed < toRemove) {
          removed++;
          return false;
        }
        return true;
      });
      localStorage.setItem('cart', JSON.stringify(newCart));
      loadCart();
      return;
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    loadCart();
  };

  const removeItem = (productId) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const newCart = cart.filter(item => item.id !== productId);
    localStorage.setItem('cart', JSON.stringify(newCart));
    loadCart();
    toast({
      title: "Produit retiré",
      description: "Le produit a été retiré de votre panier.",
    });
  };

  const clearCart = () => {
    localStorage.setItem('cart', JSON.stringify([]));
    setCartItems([]);
    toast({
      title: "Panier vidé",
      description: "Votre panier a été vidé.",
    });
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (pricing.htOf(item) * item.quantity), 0);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <ShoppingBag className="w-24 h-24 text-gray-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Votre panier est vide</h2>
            <p className="text-gray-400 mb-8">Découvrez nos produits et ajoutez-les à votre panier</p>
            <Link to="/catalogue">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3">
                Découvrir nos produits
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
            Votre Panier
          </h1>
          <Button
            onClick={clearCart}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            Vider le panier
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-gray-900 rounded-lg p-6 flex flex-col sm:flex-row gap-6"
              >
                <Link to={`/produit/${item.slug}`} className="flex-shrink-0">
                  <img loading="lazy"
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full sm:w-32 h-32 object-cover rounded-lg"
                  />
                </Link>
                
                <div className="flex-1">
                  <Link to={`/produit/${item.slug}`}>
                    <h3 className="text-white text-xl font-semibold mb-2 hover:text-purple-400 transition-colors">
                      {localizeKitName(item.name, lang)}
                    </h3>
                  </Link>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{localizeDescription(item.slug, item.description, lang)}</p>
                  {item._marking === 'custom' ? (
                    <div className="flex items-center gap-2 mb-3" data-testid="cart-item-marking">
                      {item._logoUrl && (
                        <img src={item._logoUrl.startsWith('http') ? item._logoUrl : `${process.env.REACT_APP_BACKEND_URL}${item._logoUrl}`} alt="logo" className="w-8 h-8 object-contain bg-white/90 rounded p-0.5" />
                      )}
                      <span className="inline-flex items-center gap-1 text-xs bg-purple-600/20 border border-purple-500/40 text-purple-200 rounded-full px-2.5 py-1">
                        Avec marquage{item._customText ? ` · « ${item._customText} »` : ''}
                      </span>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-xs mb-3" data-testid="cart-item-marking">Bracelet vierge (sans marquage)</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        variant="outline"
                        className="border-gray-700 text-white hover:bg-gray-800"
                        size="icon"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-white text-lg font-semibold w-8 text-center">
                        {item.quantity}
                      </span>
                      <Button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        variant="outline"
                        className="border-gray-700 text-white hover:bg-gray-800"
                        size="icon"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">
                        {pricing.fmt(pricing.ttcOn(pricing.htOf(item) * item.quantity))} TTC
                      </p>
                      <p className="text-gray-400 text-sm">
                        {pricing.fmt(pricing.htOf(item))} HT / unité
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => removeItem(item.id)}
                  className="text-red-500 hover:text-red-400 transition-colors self-start"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-lg p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-white mb-6">Résumé de la commande</h2>
              
              <div className="space-y-4 mb-6" data-testid="cart-totals">
                <div className="flex justify-between text-gray-300">
                  <span>Total HT</span>
                  <span data-testid="cart-subtotal-ht">{pricing.fmt(getTotalPrice())}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>TVA {pricing.vatRate} %</span>
                  <span data-testid="cart-vat">{pricing.fmt(pricing.vatOn(getTotalPrice()))}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Livraison</span>
                  <span className="text-green-500">Gratuite</span>
                </div>
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between text-xl font-bold text-white">
                    <span>Total TTC</span>
                    <span data-testid="cart-total-ttc">{pricing.fmt(pricing.ttcOn(getTotalPrice()))}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Prix affichés TTC. Le détail HT, TVA {pricing.vatRate} % et TTC figure sur votre facture.</p>
                </div>
              </div>
              
              <Button
                onClick={handleCheckout}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-lg font-semibold"
              >
                Passer la commande
              </Button>

              <DeliveryNotice className="mt-4" />
              
              <Link to="/catalogue">
                <Button
                  variant="outline"
                  className="w-full mt-4 border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  Continuer mes achats
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;